import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ReusableButton from "../../../CommonComponents/Button/Button";
import Notification from "../../../CommonComponents/Notification/Notification";
import { API } from "../../../Networking/API";
import { useNavigate } from "react-router-dom";
import "./CandidatePhotoCapture.css";

export default function CandidatePhotoCapture() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();
  const candidateId = localStorage.getItem("candidateId");

  const [capturedImage, setCapturedImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const photoRequired = localStorage.getItem("isPhotoRequired") === "true";

  useEffect(() => {
    // Reached directly (bookmark/refresh) on a drive that doesn't need a photo
    if (!photoRequired) {
      navigate("/quiz-section");
      return undefined;
    }
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
    } catch (error) {
      setNotify({
        isOpen: true,
        message:
          "Unable to access camera. Please allow camera permission to continue.",
        type: "error",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const maxSize = 800;
    let width = video.videoWidth;
    let height = video.videoHeight;
    if (width > maxSize || height > maxSize) {
      const scale = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, width, height);
    setCapturedImage(canvas.toDataURL("image/jpeg", 0.72));
  };

  const handleRetake = () => {
    setCapturedImage("");
  };

  const handleContinue = async () => {
    if (!capturedImage) {
      setNotify({
        isOpen: true,
        message: "Please capture your photo before continuing.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    const result = await API.uploadCandidateProfilePhoto(
      candidateId,
      capturedImage
    );

    if (result?.response?.status === true) {
      stopCamera();
      navigate("/quiz-section");
    } else {
      const apiMessage = result?.response?.message;
      const isTooLarge =
        result?.statusCode === 413 || /too large/i.test(apiMessage || "");
      setNotify({
        isOpen: true,
        message: isTooLarge
          ? "Photo file is too large. Please retake the photo."
          : apiMessage || "Failed to save photo. Please try again.",
        type: "error",
      });
    }
    setLoading(false);
  };

  return (
    <Box className="candidate-photo-page">
      <Box className="candidate-photo-card">
        <h2>Identity Verification</h2>
        <p className="candidate-photo-subtitle">
          Take a clear photo of yourself before starting the MCQ assessment.
        </p>

        <Box className="candidate-photo-preview">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="candidate-photo-video"
            style={{ display: capturedImage ? "none" : "block" }}
          />
          {capturedImage ? (
            <img
              src={capturedImage}
              alt="Captured candidate"
              className="candidate-photo-image"
            />
          ) : null}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </Box>

        <Box className="candidate-photo-actions">
          {!capturedImage ? (
            <ReusableButton
              text="Capture Photo"
              onClick={handleCapture}
              disabled={!cameraReady}
              style={{ backgroundColor: "#00728E", textTransform: "capitalize" }}
            />
          ) : (
            <>
              <ReusableButton
                text="Retake"
                onClick={handleRetake}
                style={{
                  backgroundColor: "#fff",
                  color: "#00728E",
                  border: "1px solid #00728E",
                  textTransform: "capitalize",
                  marginRight: "12px",
                }}
              />
              <ReusableButton
                text={loading ? "Saving..." : "Continue to Quiz"}
                onClick={handleContinue}
                disabled={loading}
                style={{ backgroundColor: "#00728E", textTransform: "capitalize" }}
              />
            </>
          )}
        </Box>
      </Box>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}
